import React from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';

const DashboardAccess = ({ registrationId }) => {
  const navigate = useNavigate();

  const dashboardFeatures = [
    {
      icon: 'BookOpen',
      title: 'Learning Materials',
      description: 'Access course content, videos, and resources'
    },
    {
      icon: 'Calendar',
      title: 'Schedule & Attendance',
      description: 'View upcoming sessions and track attendance'
    },
    {
      icon: 'BarChart3',
      title: 'Progress Tracking',
      description: 'Monitor your learning progress and achievements'
    },
    {
      icon: 'Users',
      title: 'Community Forum',
      description: 'Connect with batch mates and instructors'
    },
    {
      icon: 'FileText',
      title: 'Assignments & Projects',
      description: 'Submit assignments and view feedback'
    },
    {
      icon: 'Award',
      title: 'Certificates',
      description: 'Download completion certificates and badges'
    }
  ];

  const handleDashboardAccess = () => {
    // In a real application, this would navigate to the student dashboard
    window.open('https://learning.solvarsity.com/login', '_blank');
  };

  const handleStartOver = () => {
    const confirmRestart = window.confirm(
      'Are you sure you want to start a new registration? This will take you back to the profile selection page.'
    );
    
    if (confirmRestart) {
      navigate('/profile-selection-landing');
    }
  };

  return (
    <div className="bg-surface border border-border rounded-xl p-6">
      <div className="flex items-center space-x-2 mb-6">
        <Icon name="Layout" size={20} color="var(--color-primary)" strokeWidth={2} />
        <h2 className="text-xl font-semibold text-foreground">
          Access Your Learning Portal
        </h2>
      </div>

      <div className="text-center mb-6">
        <p className="text-sm text-text-secondary mb-4">
          Your personalized learning dashboard is ready! Access all your training materials, track progress, and connect with your batch.
        </p>
        
        {/* Portal Preview */}
        <div className="p-4 bg-gradient-to-br from-primary/5 to-success/5 border border-primary/20 rounded-lg mb-6">
          <div className="flex items-center justify-center space-x-2 mb-3">
            <Icon name="Monitor" size={24} color="var(--color-primary)" strokeWidth={2} />
            <span className="text-lg font-semibold text-foreground">
              Solvarsity Learning Portal
            </span>
          </div>
          <p className="text-xs text-text-secondary">
            Registration ID: <span className="font-mono font-medium">{registrationId}</span>
          </p>
        </div>
      </div>

      {/* Dashboard Features Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
        {dashboardFeatures.map((feature, index) => (
          <div
            key={index}
            className="flex items-start space-x-3 p-3 bg-muted/30 rounded-lg hover:bg-muted/50 transition-smooth"
          >
            <div className="flex-shrink-0 mt-0.5">
              <Icon name={feature.icon} size={16} color="var(--color-primary)" strokeWidth={2} />
            </div>
            <div>
              <h3 className="text-xs font-semibold text-foreground mb-1">
                {feature.title}
              </h3>
              <p className="text-xs text-text-secondary">
                {feature.description}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Action Buttons */}
      <div className="space-y-4">
        <Button
          variant="default"
          size="lg"
          onClick={handleDashboardAccess}
          iconName="ExternalLink"
          iconPosition="right"
          fullWidth
        >
          Go to Learning Portal
        </Button>

        <div className="flex flex-col sm:flex-row gap-3">
          <Button
            variant="outline"
            size="default"
            onClick={() => navigate('/profile-selection-landing')}
            iconName="Home"
            iconPosition="left"
            fullWidth
          >
            Back to Home
          </Button>
          
          <Button
            variant="ghost"
            size="default"
            onClick={handleStartOver}
            iconName="RotateCcw"
            iconPosition="left"
            fullWidth
          >
            New Registration
          </Button>
        </div>
      </div>

      {/* Login Instructions */}
      <div className="mt-6 p-4 bg-primary/5 border border-primary/20 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Info" size={18} color="var(--color-primary)" strokeWidth={2} />
          <div>
            <h3 className="text-sm font-semibold text-foreground mb-1">
              Portal Login Instructions
            </h3>
            <div className="text-xs text-text-secondary space-y-1">
              <p>• Use your registered email address as username</p>
              <p>• Temporary password sent to your email</p>
              <p>• You'll be prompted to set a new password on first login</p>
              <p>• Keep your registration ID handy for support queries</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardAccess;